import { config } from '../utils/config';

const API_BASE_URL = config.apiBaseUrl;

class ApiService {
  async makeRequest(endpoint, options = {}) {
    const url = `${API_BASE_URL}/${endpoint.startsWith('/') ? endpoint.slice(1) : endpoint}`;
    
    // Don't set Content-Type for FormData (browser will set it with boundary)
    const headers = {};
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    
    // Add JWT token from sessionStorage to Authorization header
    const accessToken = sessionStorage.getItem('access_token');
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    
    const defaultOptions = {
      credentials: 'include', // Keep for fallback compatibility
      headers: {
        ...headers,
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, defaultOptions);
      return response;
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  async fetchData(endpoint, options = {}) {
    const response = await this.makeRequest(endpoint, { method: 'GET', ...options });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // Handle blob responses for file downloads
    if (options.responseType === 'blob') {
      return response.blob();
    }
    
    return response.json();
  }

  async postData(endpoint, data, options = {}) {
    const response = await this.makeRequest(endpoint, {
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
    });
    
    if (!response.ok) {
      // For 400 and 409 errors, parse JSON and throw with data attached
      if (response.status === 400 || response.status === 409) {
        try {
          const errorData = await response.json();
          // If returnErrorData option is true, return instead of throw (for bulk uploads)
          if (options.returnErrorData) {
            return errorData;
          }
          // Create error with response data attached
          const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
          error.responseData = errorData;
          error.status = response.status;
          throw error;
        } catch (parseError) {
          if (parseError.responseData) throw parseError; // Re-throw if already processed
          throw new Error(response.status === 409 
            ? 'Conflict: Resource already exists' 
            : `HTTP error! status: ${response.status}`);
        }
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Specific method for bulk uploads that returns error data instead of throwing
  async postBulkUpload(endpoint, formData) {
    const response = await this.makeRequest(endpoint, {
      method: 'POST',
      body: formData,
    });
    
    // Always parse and return the JSON response (success or error)
    try {
      return await response.json();
    } catch (parseError) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  async putData(endpoint, data) {
    const response = await this.makeRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  async deleteData(endpoint, data) {
    const response = await this.makeRequest(endpoint, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: data ? JSON.stringify(data) : undefined,
    });
  
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  
    // Some DELETE responses are empty, handle safely
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

 async sendEmail(emailData) {
  const response = await this.makeRequest('/send-email', {
    method: 'POST',
    body: JSON.stringify(emailData),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}
 async uploadProfileImage(formData) {
  const response = await this.makeRequest('/upload-profile-pic', {
    method: 'POST',
    body: formData,           // FormData passed directly
    headers: {}               // NO content-type for FormData
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}



  /**
   * Get user details from backend
   */
  async getUserDetails() {
    try {
      const response = await this.makeRequest('/user-details', {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('📋 User details retrieved:', result);
      return result;
    } catch (error) {
      console.error('❌ Error fetching user details:', error);
      throw error;
    }
  }

  /**
   * Submit assessment data
   */
  async submitAssessment(assessmentData) {
    try {
      const response = await this.makeRequest('/assessments', {
        method: 'POST',
        body: JSON.stringify(assessmentData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Assessment submitted:', result);
      return result;
    } catch (error) {
      console.error('❌ Error submitting assessment:', error);
      throw error;
    }
  }

  /**
   * Get assessments list
   */
  async getAssessments() {
    try {
      const response = await this.makeRequest('/assessments', {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('📋 Assessments retrieved:', result);
      return result;
    } catch (error) {
      console.error('❌ Error fetching assessments:', error);
      throw error;
    }
  }

  /**
   * Get dashboard data
   */
  async getDashboardData() {
    try {
      const response = await this.makeRequest('/dashboard', {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('📊 Dashboard data retrieved:', result);
      return result;
    } catch (error) {
      console.error('❌ Error fetching dashboard data:', error);
      throw error;
    }
  }
}



const apiService = new ApiService();
export default apiService;
